import { apiFetch, ApiError } from "../api";
import { getLocalBlob, isLocalFileId, isNetworkError, listOutbox, removeOutbox, updateOutbox, type OutboxItem } from "./outbox";

/**
 * Replays the offline outbox against the API, oldest first:
 *   photos kept on the device -> reserve upload -> PUT to storage -> verify,
 *   then POST the submission with Idempotency-Key = item id.
 * Stops (and keeps items pending) as soon as the network is gone again or the
 * token is rejected; marks an item failed when the server refuses it for good.
 */
export interface SyncResult {
  sent: number;
  failed: number;
  stopped?: "offline" | "unauthorized";
}

const MAX_ATTEMPTS = 8;
const RETRYABLE_STATUS = new Set([500, 502, 503, 504]);

let running: Promise<SyncResult> | null = null;

export function syncOutbox(token: string): Promise<SyncResult> {
  if (running) return running;
  running = run(token).finally(() => {
    running = null;
  });
  return running;
}

class PermanentError extends Error {}

async function uploadLocal(token: string, localId: string): Promise<string> {
  const stored = await getLocalBlob(localId);
  if (!stored) throw new PermanentError("A photo is no longer on this device");
  const { fileId, uploadUrl } = await apiFetch<{ fileId: string; uploadUrl: string }>("POST", "/api/v1/files/uploads", token, { mimeType: stored.mimeType, sizeBytes: stored.blob.size });
  const put = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": stored.mimeType }, body: stored.blob });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);
  const verified = await apiFetch<{ id: string; status: string }>("POST", `/api/v1/files/${fileId}/complete`, token);
  if (verified.status !== "verified") throw new PermanentError("A photo could not be verified");
  return fileId;
}

/** Body with every local photo id replaced by its server id (uploading what is still missing). */
async function resolveBody(token: string, item: OutboxItem): Promise<Record<string, unknown>> {
  const uploaded = { ...item.uploaded };
  const body = structuredClone(item.body);
  const images = body.images as { fileId: string; label: string }[] | undefined;
  if (Array.isArray(images)) {
    for (const img of images) {
      if (!isLocalFileId(img.fileId)) continue;
      if (!uploaded[img.fileId]) {
        uploaded[img.fileId] = await uploadLocal(token, img.fileId);
        await updateOutbox(item.id, { uploaded });
      }
      img.fileId = uploaded[img.fileId];
    }
  }
  return body;
}

async function run(token: string): Promise<SyncResult> {
  const result: SyncResult = { sent: 0, failed: 0 };
  const items = (await listOutbox()).filter((i) => i.status === "pending");
  for (const item of items) {
    try {
      const body = await resolveBody(token, item);
      await apiFetch("POST", item.path, token, body, { "Idempotency-Key": item.id });
      await removeOutbox(item.id);
      result.sent++;
    } catch (e) {
      const err = e as Error;
      const attempts = item.attempts + 1;
      if (isNetworkError(e)) {
        await updateOutbox(item.id, { attempts, lastError: "No connection" });
        result.stopped = "offline";
        return result;
      }
      if (e instanceof ApiError && e.status === 401) {
        await updateOutbox(item.id, { attempts, lastError: "Please sign in again" });
        result.stopped = "unauthorized";
        return result;
      }
      const retryable = !(e instanceof PermanentError) && (!(e instanceof ApiError) || RETRYABLE_STATUS.has(e.status));
      if (retryable && attempts < MAX_ATTEMPTS) {
        await updateOutbox(item.id, { attempts, lastError: err.message });
        continue;
      }
      await updateOutbox(item.id, { attempts, status: "failed", lastError: err.message });
      result.failed++;
    }
  }
  return result;
}
